import { Fragment, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, money, STATUS_LABELS } from "@/lib/api";
import { Badge, Card, toneFor, useAdmin } from "@/admin/AdminShell";
import { LyricsDisplay } from "@/components/site/Audio";

const REVIEW = [
  ["recipient_name", "Recipient name checked"], ["pronunciation", "Pronunciation reviewed"], ["story_details", "Important story details represented"],
  ["lyrics_match", "Lyrics reasonably match the approved version"], ["no_truncation", "No obvious truncation"], ["audio_plays", "Audio plays correctly"],
  ["no_inappropriate", "No obviously inappropriate content"], ["occasion_tone", "Occasion and emotional tone appropriate"],
  ["language_delivery", "Song language delivery reviewed"], ["candidate_selected", "Correct candidate selected"],
];

const Json = ({ data }) => <pre className="max-h-64 overflow-auto rounded-lg bg-slate-50 p-3 text-xs">{JSON.stringify(data, null, 2)}</pre>;

const ReviewPanel = ({ order, gen, act }) => {
  const qr = order.quality_review || {};
  const [check, setCheck] = useState(qr.checklist || {});
  const [cand, setCand] = useState(qr.selected_candidate_id || "");
  const [notes, setNotes] = useState(qr.notes || "");
  const save = () => act("review", { checklist: check, selected_candidate_id: cand || null, notes }, "Review saved");
  return (
    <Card title="Quality review" testid="admin-quality-review">
      <div className="space-y-3">
        {gen.candidates.map((c) => (
          <label key={c.id} className="flex items-center gap-3 rounded-lg border p-3 text-sm">
            <input type="radio" name="cand" checked={cand === c.id} onChange={() => setCand(c.id)} data-testid={`admin-candidate-${c.id}`} />
            <div className="min-w-0 flex-1"><p className="font-medium">{c.title} {c.dev_sample && <Badge tone="amber">DEV SAMPLE</Badge>}</p>
              <audio controls preload="none" src={c.audio_url} className="mt-2 w-full" /></div>
            <span className="text-xs text-slate-500">{c.duration ? `${Math.round(c.duration)}s` : ""}</span>
          </label>
        ))}
        <div className="grid gap-1 sm:grid-cols-2">
          {REVIEW.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!check[k]} onChange={(e) => setCheck({ ...check, [k]: e.target.checked })} data-testid={`admin-review-${k}`} />{l}</label>)}
        </div>
        <textarea className="w-full rounded-lg border p-2 text-sm" rows={2} placeholder="Review notes" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="admin-review-notes" />
        <div className="flex flex-wrap gap-2">
          <button className="rounded-lg border px-3 py-2 text-sm" onClick={save} data-testid="admin-review-save">Save review</button>
          <button className="rounded-lg bg-green-700 px-3 py-2 text-sm text-white" onClick={async () => { await save(); act("approve-delivery", {}, "Approved and delivered"); }} data-testid="admin-quality-approve-button">Approve & deliver</button>
        </div>
      </div>
    </Card>
  );
};

const Actions = ({ order, act, user }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [ref, setRef] = useState("");
  const btn = (label, path, body, testid, confirmMsg) => (
    <button className="rounded-lg border px-3 py-2 text-sm hover:bg-slate-50" data-testid={testid}
      onClick={() => (!confirmMsg || window.confirm(confirmMsg)) && act(path, body, `${label} done`)}>{label}</button>
  );
  return (
    <Card title="Actions" testid="admin-actions">
      <input className="mb-3 w-full rounded-lg border px-3 py-2 text-sm" placeholder="Reason (for regenerate / flag)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-action-reason" />
      <div className="flex flex-wrap gap-2">
        {btn("Regenerate", "regenerate", { reason }, "admin-regenerate", "Submit a new generation? This uses provider credits.")}
        {btn("Retry failed job", "retry", {}, "admin-retry")}
        {btn("Resend delivery email", "resend-email", {}, "admin-resend-email")}
        {btn("Revoke gift link", "revoke-gift", {}, "admin-revoke-gift")}
        {btn("Rotate customer link", "rotate-access", {}, "admin-rotate-access")}
        {btn("Mark fulfillment issue", "flag", { reason }, "admin-flag")}
        {btn("Back to quality review", "set-status", { status: "quality_review" }, "admin-set-review")}
        {btn("Reconcile PayPal", "reconcile", {}, "admin-reconcile")}
        {order.payment_status !== "completed" && btn("Cancel order", "set-status", { status: "cancelled" }, "admin-cancel", "Cancel this unpaid order?")}
      </div>
      <div className="mt-4 flex gap-2">
        <input className="flex-1 rounded-lg border px-3 py-2 text-sm" placeholder="Contact / internal note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input" />
        <button className="rounded-lg border px-3 py-2 text-sm" onClick={() => { act("notes", { note }, "Note added"); setNote(""); }} data-testid="admin-note-save">Add note</button>
      </div>
      {user.role === "owner" && order.payment_status === "completed" && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm font-medium text-red-800">Refund</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button className="rounded-lg bg-red-700 px-3 py-2 text-sm text-white" data-testid="admin-refund-paypal" onClick={() => window.confirm("Issue a PayPal refund?") && act("refund", { mode: "paypal" }, "Refund confirmed")}>Refund via PayPal</button>
            <input className="rounded-lg border px-3 py-2 text-sm" placeholder="External confirmation ref" value={ref} onChange={(e) => setRef(e.target.value)} data-testid="admin-refund-reference" />
            <button className="rounded-lg border border-red-300 px-3 py-2 text-sm" data-testid="admin-refund-external" onClick={() => act("refund", { mode: "external", reference: ref }, "External refund recorded")}>Record external refund</button>
          </div>
        </div>
      )}
    </Card>
  );
};

const Revisions = ({ order, act }) => (
  <Card title="Revisions" testid="admin-revisions">
    {order.revisions.length === 0 && <p className="text-sm text-slate-500">No revision requests.</p>}
    {order.revisions.map((r) => (
      <div key={r.id} className="mb-3 rounded-lg border p-3 text-sm">
        <p><Badge tone={toneFor(r.status)}>{r.status}</Badge> {r.created_at.slice(0, 16)} · category: {r.category || "unclassified"} · consumes allowance: {String(r.consumes)}</p>
        <p className="mt-2">{r.notes}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <select className="rounded border px-2 py-1" defaultValue="" onChange={(e) => e.target.value && act(`revisions/${r.id}`, { action: "classify", category: e.target.value }, "Classified")} data-testid={`admin-revision-classify-${r.id}`}>
            <option value="">Classify…</option><option value="preference_change">Customer preference change</option><option value="provider_failure">Provider failure</option>
            <option value="delivery_failure">Delivery failure</option><option value="incorrect_fulfillment">Incorrect fulfillment</option>
          </select>
          {r.status === "requested" && <button className="rounded border px-2 py-1" onClick={() => act(`revisions/${r.id}`, { action: "start" }, "Revision generation started")} data-testid={`admin-revision-start-${r.id}`}>Start regeneration</button>}
          {r.status === "requested" && <button className="rounded border px-2 py-1" onClick={() => act(`revisions/${r.id}`, { action: "decline" }, "Declined")} data-testid={`admin-revision-decline-${r.id}`}>Decline</button>}
        </div>
      </div>
    ))}
  </Card>
);

const ManualLyrics = ({ act }) => {
  const [f, setF] = useState({ title: "", text: "" });
  return (
    <Card title="Draft lyrics manually">
      <input className="mb-2 w-full rounded-lg border px-3 py-2 text-sm" dir="auto" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="admin-manual-title" />
      <textarea rows={8} className="w-full rounded-lg border px-3 py-2 text-sm" dir="auto" placeholder="[Verse 1]…" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="admin-manual-text" />
      <button className="mt-2 rounded-lg border px-3 py-2 text-sm" onClick={() => act("lyrics", f, "Lyrics version added")} data-testid="admin-manual-save">Save as new version</button>
    </Card>
  );
};

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAdmin();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/admin/orders/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  const act = async (path, body, okMsg) => {
    try { await api.post(`/admin/orders/${id}/${path}`, body); toast.success(okMsg); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;
  const o = d.order;
  const gen = d.generations.find((g) => g.id === o.current_generation_id);
  const lang = o.song_language;
  return (
    <div className="space-y-6" data-testid="admin-order-detail">
      <Link to="/admin/orders" className="text-sm text-violet-700 underline">← Orders</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{o.reference}</h1>
        <Badge tone={toneFor(o.payment_status)} testid="admin-detail-payment">{o.payment_status}</Badge>
        <Badge tone={toneFor(o.fulfillment_status)} testid="admin-detail-fulfillment">{STATUS_LABELS[o.fulfillment_status]}</Badge>
        {o.mode === "development" && <Badge tone="amber">development order</Badge>}
      </div>
      {o.attention_reasons.length > 0 && <Card title="Needs review"><ul className="list-disc pl-5 text-sm text-red-800">{o.attention_reasons.map((a, i) => <li key={i}>{a.at.slice(0, 16)} — {a.reason}</li>)}</ul></Card>}
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Brief" testid="admin-brief">
          <dl className="grid grid-cols-3 gap-2 text-sm">
            {[["Recipient", `${o.recipient.name}${o.recipient.preferred_spelling ? ` (${o.recipient.preferred_spelling})` : ""}`], ["Pronunciation", o.recipient.pronunciation], ["Relationship", o.recipient.relationship],
              ["From", o.sender_name], ["Event date", o.event_date], ["Occasion", `${o.occasion_label} (${o.occasion_category})${o.custom_occasion_label ? ` — ${o.custom_occasion_label}` : ""}`],
              ["Tone / style", `${o.tone} / ${o.style_label}`], ["Song language", `${lang.english_name} · ${lang.native_name} · ${lang.code}${lang.rtl ? " · RTL" : ""}`],
              ["Story language", o.story_language], ["Buyer", o.buyer_email], ["Price", o.price ? money(o.price, o.currency) : "—"],
              ["Terms", o.terms_version ? `${o.terms_version} @ ${o.terms_accepted_at?.slice(0, 16)}` : "—"], ["Approved lyrics", o.approved_lyrics_version ? `v${o.approved_lyrics_version}` : "—"],
            ].map(([k, v]) => <Fragment key={k}><dt className="text-slate-500">{k}</dt><dd className="col-span-2 bidi-isolate" dir="auto">{v || "—"}</dd></Fragment>)}
          </dl>
          <p className="mt-4 text-xs font-semibold uppercase text-slate-500">Story</p>
          <p className="mt-1 whitespace-pre-wrap text-sm" dir="auto">{o.story}</p>
          {["must_include", "avoid", "phrases", "dedication"].map((k) => o[k] && <p key={k} className="mt-2 text-sm" dir="auto"><span className="text-slate-500">{k}:</span> {o[k]}</p>)}
        </Card>
        <Card title={`Lyrics versions (${o.lyrics_versions.length})`} testid="admin-lyrics-versions">
          {o.lyrics_versions.slice().reverse().map((v) => (
            <details key={v.version} className="mb-2 rounded-lg border p-3" open={v.version === o.approved_lyrics_version}>
              <summary className="cursor-pointer text-sm">v{v.version} · {v.source} · {v.language_id}{v.language_variant && `/${v.language_variant}`} {v.version === o.approved_lyrics_version && <Badge tone="green">approved</Badge>} {o.delivered?.lyrics_version === v.version && <Badge tone="violet">delivered</Badge>}</summary>
              <p className="mt-2 font-medium" dir="auto">{v.title}</p>
              <div className="mt-2 text-sm"><LyricsDisplay text={v.text} language={lang} /></div>
            </details>
          ))}
        </Card>
      </div>
      {o.payment_status !== "completed" && <ManualLyrics act={act} />}
      {gen && ["quality_review", "needs_attention"].includes(o.fulfillment_status) && gen.candidates.length > 0 && <ReviewPanel order={o} gen={gen} act={act} />}
      <div className="grid gap-6 xl:grid-cols-2">
        <Actions order={o} act={act} user={user} />
        <Revisions order={o} act={act} />
      </div>
      <Card title={`Generations (${d.generations.length})`} testid="admin-generations">
        {d.generations.map((g) => (
          <div key={g.id} className="mb-3 rounded-lg border p-3 text-sm">
            <p><Badge tone={toneFor(g.status)}>{g.status}</Badge> {g.kind} · provider {g.provider || "—"} · job <code>{g.provider_job_id || "—"}</code> · lyrics v{g.lyrics_version} · attempts {g.attempts} · provider status {g.provider_status || "—"} · est. credits {g.estimated_credits ?? "—"}</p>
            <p className="text-xs text-slate-500">submitted {g.submitted_at?.slice(0, 19) || "—"} · completed {g.completed_at?.slice(0, 19) || "—"} · candidates {g.candidates.length}{o.delivered?.generation_id === g.id && ` · delivered candidate ${o.delivered.candidate_id}`}</p>
            {g.errors.length > 0 && <Json data={g.errors} />}
          </div>
        ))}
      </Card>
      <div className="grid gap-6 xl:grid-cols-2">
        <Card title="Payments"><Json data={d.payments} /></Card>
        <Card title="Delivery events & notes"><Json data={{ delivery_events: o.delivery_events, admin_notes: o.admin_notes, gift: o.gift }} /></Card>
        <Card title={`Emails (${d.emails.length})`} testid="admin-order-emails">{d.emails.map((e) => <details key={e.id} className="mb-1 text-sm"><summary>{e.created_at.slice(0, 16)} · {e.template} · {e.status}</summary><pre className="whitespace-pre-wrap text-xs">{e.subject}{"\n\n"}{e.body}</pre></details>)}</Card>
        <Card title="Jobs & audit"><Json data={{ jobs: d.jobs.map((j) => ({ type: j.type, status: j.status, attempts: j.attempts, last_error: j.last_error, next: j.next_attempt_at })), audit: d.audit }} /></Card>
      </div>
    </div>
  );
}
